import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonService } from '../../core/common-service/common.service';
import { RemotePriceChangeService } from '../remote-price-change-service/remote-price-change.service';
import { FilterBarService } from '../../shared/filter-bar-service/filter-bar.service';
import { iVendLookup, RegulatoryStatusEnum, RPCTrackStatusExcelItem, RPCTrackStatusItem, RPCTrackStatusMetadata, RPCTrackStatusResponse, RPCTrackStatusSaveResponse, RPCTrackStatusSummaryResponse, RpcTrackStatusSearchRequest, RpcTrackStatusSummary, UpgradeNotNeededReason 
  } from '../remote-price-change-service/remote-price-change.model';
import { SecurityService } from '../../core/security/security.service';
import { ISubscription } from 'rxjs-compat/Subscription';
import { ExcelService } from '../../core/excel-service/excel.service';
import { Moment } from 'moment';
import * as moment from 'moment';
import { connectableObservableDescriptor } from 'rxjs/internal/observable/ConnectableObservable';
import { trigger } from '@angular/animations';

@Component({
  selector: 'app-rpc-status',
  templateUrl: './rpc-status.component.html',
  styleUrls: ['./rpc-status.component.scss']
})
export class RpcStatusComponent implements OnInit, OnDestroy {
  Math = Math;
  isRefreshingPage: boolean;
  pageSize: number = 1000;
  pageNumber: number = 1;
  majorAccountId: number;
  routeIDList: number[];
  accLocList: number[];
  isProcessing: boolean = false;
  showInfoPopover: boolean = false;
  infoPopoverContent: string = `
    <p><strong>Upgrade Needed:</strong> Initial assessment to determine if the machine requires an upgrade.</p>
    <p><strong>If Upgrade Needed = Yes:</strong> Machines that require upgrade. If a machine upgrade has been completed by a technician:</p>
    <ul>
      <li>Change Upgrade Complete = <strong>Yes</strong></li>
      <li>Enter Technician's First and Last Name (Labor Allocation)</li>
      <li class="warning-text">*DO NOT CHANGE UPGRADE NEEDED BACK TO NO*</li>
    </ul>
    <p>If a machine has not been completed by a technician:</p>
    <ul>
      <li>Change Upgrade Complete = <strong>NO</strong> or <strong>N/A</strong></li>
    </ul>
    <p><strong>If Upgrade Needed = No:</strong> Machines that are not eligible for an upgrade. You must select a Reason why that machine can't be upgraded.</p>
    <p><strong>Data Correction:</strong></p>
    <p>Upgrade Needed = No, and Upgrade Complete = Yes, the row will highlight in <span class="orange-highlight">Orange</span>, and it is recommended to correct it accordingly.</p>
    <ul>
      <li>Either change Upgrade Needed = No and select a Reason for no upgrade</li>
      <li>Or Change Upgrade Needed = Yes, Upgrade Complete = Yes, and enter the First and Last name of the technician who completed the machine upgrade.</li>
    </ul>`;
  responseMessage: string;
  errorMessage: string;
  tableIsDirty: boolean;
  userID: number;
  params = ['routes', 'accounts', 'rpcStatus', 'buttons'];
  filterPosition = 'in';
  view = 'list';
  isMobile = false;
  enableSaveButton: boolean;
  totalNumberOfItems: number;
  
  metadata: RPCTrackStatusMetadata;
  rpcStatusItems: RPCTrackStatusItem[] = new Array();
  ivendLookups: iVendLookup[] = new Array();
  rpcTrackStatusItemSubject: ISubscription;
  rpcTrackStatusFilterSubject: ISubscription;
  rpcStatusFilterRequest: RpcTrackStatusSearchRequest = new RpcTrackStatusSearchRequest();
  upgradeNotNeededReasons: UpgradeNotNeededReason[] = new Array();

  summaryData: RpcTrackStatusSummary;
  summaryError: string;
  isLoadingSummary: boolean;
  summaryExpanded: boolean = true;
  
  constructor(private _common: CommonService,
    private _rpcService: RemotePriceChangeService,
    private _filterBarService: FilterBarService,
    private _security: SecurityService,
    private _excel: ExcelService,) 
  {
    this._common.setCurrentPage('RPC Tracking Status');            
  }

  ngOnInit() {
    this.tableIsDirty = false;
    this.enableSaveButton = true;
    this.rpcStatusItems = new Array();
    this.setUpSubjects();
  }

  ngOnDestroy(): void {
    this._filterBarService.ResetFilterBar();
    if (this.rpcTrackStatusItemSubject) {
      this.rpcTrackStatusItemSubject.unsubscribe();
    }
    if (this.rpcTrackStatusFilterSubject) {
      this.rpcTrackStatusFilterSubject.unsubscribe();
    }
    this._filterBarService.rpcStatusFilters.next(new RpcTrackStatusSearchRequest());
  }

  private setUpSubjects() {
    this.isRefreshingPage = true;
    this.userID = this._security.getUserID();
    this._filterBarService.selectedMajorAcct.subscribe(data => {
      this.majorAccountId = data;
    });
    this._rpcService.Initialize();
    this._filterBarService.ResetFilterBar();
    // make a stripped-down call to API just to get the lookups
    this.rpcTrackStatusItemSubject = this._rpcService.GetRPCTrackStatusItems(this.dummyRequestObjectForLookups()).subscribe(
      (data: RPCTrackStatusResponse) => {
        console.log("SEARCH REVIEW Result::",data);
        this.ivendLookups = data.iVendLookups;
        this.setItemsFieldsFromLookups(this.rpcStatusItems, this.ivendLookups);
        this.isRefreshingPage = false;
      }),
      (error)=> {
        this.isRefreshingPage = false;
      };
    // updates to filter bar show up here
    this.rpcTrackStatusFilterSubject= this._filterBarService.rpcStatusFilterUpdate.subscribe((data: RpcTrackStatusSearchRequest) => {
      this.rpcStatusFilterRequest = data;  
    });

    this._filterBarService.upgradeNotNeededReasons.subscribe((data: UpgradeNotNeededReason[]) => {
      this.upgradeNotNeededReasons = data;
    });

    this._filterBarService.resetFilters.subscribe(() => {
      this.pageNumber = 1;
    });
  }

  private loadSummaryData() {
    this.isLoadingSummary = true;
    this.summaryError = null;

    if (!this._common.operation || !this._common.operation.operationNo) {
      console.warn('loadSummaryData: operation not available yet, skipping summary load.');
      this.summaryError = 'An error occurred while loading summary data. Please try again.';
      this.isLoadingSummary = false;
      return;
    }

    const costCenterID = Number(this._common.operation.operationNo);

    if (isNaN(costCenterID) || costCenterID === 0) {
      console.warn(`loadSummaryData: invalid costCenterID (${this._common.operation.operationNo}), skipping summary load.`);
      this.summaryError = 'An error occurred while loading summary data. Please try again.';
      this.isLoadingSummary = false;
      return;
    }

    this._rpcService.GetRPCTrackStatusSummary(costCenterID).subscribe(
      (data: RPCTrackStatusSummaryResponse) => {
        this.summaryData = data.summary;
        this.isLoadingSummary = false;
      },
      (error) => {
        console.error('loadSummaryData: API call failed', error);
        this.summaryError = 'An error occurred while loading summary data. Please try again.';
        this.isLoadingSummary = false;
      }
    );
  }

  onSummaryLinkClick(category: string, regulatoryType?: string) {
    const request = new RpcTrackStatusSearchRequest();
    request.costCenterID = Number(this._common.operation.operationNo);
    request.pageNumber = 1;
    request.pageSize = this.pageSize;
    request.sortColumn = this.rpcStatusFilterRequest.sortColumn || 'locationID';
    request.sortDirection = this.rpcStatusFilterRequest.sortDirection || 'ASC';

    switch (category) {
      case 'noUpgradeNeeded':
        request.upgradeNeededLookupIDs = [1];
        if (regulatoryType === 'reg') {
          request.regulatoryStatus = RegulatoryStatusEnum.Regulatory;
        } else if (regulatoryType === 'nonReg') {
          request.regulatoryStatus = RegulatoryStatusEnum.NonRegulatory;
        } else if (regulatoryType === 'notOnALocation') {
          request.regulatoryStatus = RegulatoryStatusEnum.NotOnALocation;
        } else {
          request.regulatoryStatus = RegulatoryStatusEnum.All;
        }
        break;
      case 'upgradeCompleted':
        request.upgradeNeededLookupIDs = [2];
        request.upgradeCompleteLookupIDs = [2];
        if (regulatoryType === 'reg') {
          request.regulatoryStatus = RegulatoryStatusEnum.Regulatory;
        } else if (regulatoryType === 'nonReg') {
          request.regulatoryStatus = RegulatoryStatusEnum.NonRegulatory;
        } else if (regulatoryType === 'notOnALocation') {
          request.regulatoryStatus = RegulatoryStatusEnum.NotOnALocation;
        } else {
          request.regulatoryStatus = RegulatoryStatusEnum.All;
        }
        break;
      case 'remainingUpgrade':
        request.upgradeNeededLookupIDs = [2];
        request.upgradeCompleteLookupIDs = [1, 3];
        if (regulatoryType === 'reg') {
          request.regulatoryStatus = RegulatoryStatusEnum.Regulatory;
        } else if (regulatoryType === 'nonReg') {
          request.regulatoryStatus = RegulatoryStatusEnum.NonRegulatory;
        } else if (regulatoryType === 'notOnALocation') {
          request.regulatoryStatus = RegulatoryStatusEnum.NotOnALocation;
        } else {
          request.regulatoryStatus = RegulatoryStatusEnum.All;
        }
        break;
      case 'all':
        // No upgrade filter — show all statuses for the selected regulatory type
        if (regulatoryType === 'reg') {
          request.regulatoryStatus = RegulatoryStatusEnum.Regulatory;
        } else if (regulatoryType === 'nonReg') {
          request.regulatoryStatus = RegulatoryStatusEnum.NonRegulatory;
        } else if (regulatoryType === 'notOnALocation') {
          request.regulatoryStatus = RegulatoryStatusEnum.NotOnALocation;
        } else {
          request.regulatoryStatus = RegulatoryStatusEnum.All;
        }
        break;
      case 'notOnALocation':
        request.regulatoryStatus = RegulatoryStatusEnum.NotOnALocation;
        break;
    }

    // Push filter state to the filter panel UI so it reflects the hyperlink selection once
    this._filterBarService.rpcStatusFilters.next(request);
    this.pageNumber = 1;
    this.isRefreshingPage = true;
    this.responseMessage = '';
    this.errorMessage = '';

    this.rpcTrackStatusItemSubject = this._rpcService.GetRPCTrackStatusItems(request).subscribe(
      (data: RPCTrackStatusResponse) => {
        this.metadata = data.metadata;
        this.rpcStatusItems = data.items;
        this.ivendLookups = data.iVendLookups;
        this.setItemsFieldsFromLookups(this.rpcStatusItems, this.ivendLookups);
        this.totalNumberOfItems = this.metadata.totalItemCount;
        this.isRefreshingPage = false;
        if (data.metadata.totalItemCount === 0) {
          this.responseMessage = 'There are no results found';
        }
      },
      (error) => {
        this.errorMessage = `An error has occurred.  Please try again.  Message=${error}`;
        this.isRefreshingPage = false;
      }
    );
  }

  private dummyRequestObjectForLookups() {
    var result = new RpcTrackStatusSearchRequest();
    result.costCenterID = 5141;
    result.pageNumber = 1;
    result.pageSize = 1;
    result.sortColumn = 'locationID';
    result.sortDirection = 'asc';
    return result;
  }

  private setItemsFieldsFromLookups(items: RPCTrackStatusItem[], iVendLookups: iVendLookup[]) {
    // Set field values from lookups
    for (let index = 0; index <  items.length; index++) {
      const item = items[index];
      this.setItemFieldsFromLookups(item, iVendLookups);

      // Default isRegulatory to false if missing from API response
      if (item.isRegulatory == null) {
        item.isRegulatory = false;
        console.warn(`RPCTrackStatusItem at index ${index} (deviceNo: ${item.deviceNo}) is missing 'isRegulatory' field. Defaulting to false.`);
      }

      item.accountLocation = item.accountName + '\r\n' + item.locationName;
      item.vendorCode = item.vendorCode.slice(0,5);
    }
  }

  private setItemFieldsFromLookups(item: RPCTrackStatusItem, iVendLookups: iVendLookup[]) {
    var upgradeStepsLookup = iVendLookups.find(x=>x.iVendLookupID===item.upgradeStepsLookupID);
    item.upgradeSteps = upgradeStepsLookup === undefined ? `Upgrade step ID ${item.upgradeStepsLookupID} does not exist` : upgradeStepsLookup.description;

    var testingStatusLookup = iVendLookups.find(x=>x.typeName==='RPCTrackStatusTestingStatus' && x.iVendLookupID===item.testingStatus);
    item.testingStatusDisplay = testingStatusLookup === undefined ? `Testing status TypeName=RPCTrackStatusTestingStatus and ID ${item.testingStatus} not found` : testingStatusLookup.enumerationValue;

    var upgradeNeededLookup = iVendLookups.find(x=>x.typeName==='RPCTrackStatusUpgradeNeeded' && x.iVendLookupID==item.upgradeNeeded);
    item.upgradeNeededDisplay = upgradeNeededLookup === undefined ? `Upgrade needed status ID ${item.upgradeNeeded} not found` : upgradeNeededLookup.enumerationValue;

    var upgradeCompleteLookup = iVendLookups.find(x=>x.typeName=='RPCTrackStatusUpgradeComplete' && x.iVendLookupID==item.upgradeComplete);
    item.upgradeCompleteDisplay = upgradeCompleteLookup === undefined ? `Testing status ID ${item.upgradeComplete} not found` : upgradeCompleteLookup.enumerationValue;

    var testingCompleteLookup = iVendLookups.find(x=>x.typeName=='RPCTrackStatusTestingComplete' && x.iVendLookupID==item.testingComplete);
    item.testingCompleteDisplay = testingCompleteLookup === undefined ? `Testing complete status ID ${item.testingComplete} not found` : testingCompleteLookup.enumerationValue;

    if (item.upgradeNotNeededReasonLookupID) {
      const matchedReason = this.upgradeNotNeededReasons.find(r => r.iVendLookupID === item.upgradeNotNeededReasonLookupID);
      if (matchedReason) {
        item.upgradeNotNeededReason = matchedReason.description;
      }
    } else if (item.upgradeNotNeededReason) {
      const matchedReason = this.upgradeNotNeededReasons.find(r => r.description === item.upgradeNotNeededReason);
      item.upgradeNotNeededReasonLookupID = matchedReason ? matchedReason.iVendLookupID : null;
    }
  }

  private onApplyFilters(resetPage: boolean) {   
    if (resetPage) {
      this.pageNumber = 1;
    }
    this.responseMessage = "";
    this.errorMessage = "";
    this.populateNumberFieldsFromLookups();
    this.rpcStatusFilterRequest.costCenterID = Number(this._common.operation.operationNo);
    this.rpcStatusFilterRequest.pageNumber = this.pageNumber;
    this.rpcStatusFilterRequest.pageSize = this.pageSize;
    this.rpcStatusFilterRequest.majorAccountId = this.majorAccountId;
    this.accLocList = this.getAccLocationArray();
    this.routeIDList = this.getRouteArray();
    this.rpcStatusFilterRequest.accountLocationList = this.accLocList;
    this.rpcStatusFilterRequest.routeIDList = this.routeIDList;
    this.isRefreshingPage = true;
    this.loadSummaryData();
    console.log(`Search filter: ${JSON.stringify(this.rpcStatusFilterRequest)}`);
    this.rpcTrackStatusItemSubject = this._rpcService.GetRPCTrackStatusItems(this.rpcStatusFilterRequest).subscribe(
      (data: RPCTrackStatusResponse) => {
        console.log("SEARCH REVIEW Result::",data);
        this.metadata = data.metadata;
        this.rpcStatusItems = data.items;
        this.ivendLookups = data.iVendLookups;
        this.setItemsFieldsFromLookups(this.rpcStatusItems, this.ivendLookups);
        this.totalNumberOfItems = this.metadata.totalItemCount;
        this.isRefreshingPage = false;
        if (data.metadata.totalItemCount === 0) {
          this.responseMessage = `There are no results found`;
        }
      }),
      (error)=> {
        this.errorMessage = `An error has occurred.  Please try again.  Message=${error}`;
        this.isRefreshingPage = false;
      };
  }

  private populateNumberFieldsFromLookups() {
    if (this.rpcStatusFilterRequest.upgradeSteps !== undefined && this.rpcStatusFilterRequest.upgradeSteps !== null) {
      var usLookup = this.ivendLookups.find(lookup => lookup.description === this.rpcStatusFilterRequest.upgradeSteps);
      this.rpcStatusFilterRequest.upgradeStepsLookupID = usLookup.iVendLookupID;
    } else {
      this.rpcStatusFilterRequest.upgradeStepsLookupID = null;
    }

    if (this.rpcStatusFilterRequest.upgradeNotNeededReason !== undefined && this.rpcStatusFilterRequest.upgradeNotNeededReason !== null) {
      var unnrLookup = this.upgradeNotNeededReasons.find(r => r.description === this.rpcStatusFilterRequest.upgradeNotNeededReason);
      this.rpcStatusFilterRequest.upgradeNotNeededReasonLookupID = unnrLookup ? unnrLookup.iVendLookupID : null;
    } else {
      this.rpcStatusFilterRequest.upgradeNotNeededReasonLookupID = null;
    }
    
    this.rpcStatusFilterRequest.upgradeNeededLookupIDs = new Array();
    this.rpcStatusFilterRequest.upgradeNeeded.forEach(x => {
      var unLookup = this.ivendLookups.find(lookup => lookup.typeName === 'RPCTrackStatusUpgradeNeeded' && lookup.enumerationValue === x);
      this.rpcStatusFilterRequest.upgradeNeededLookupIDs.push(unLookup.iVendLookupID);
    });
    this.rpcStatusFilterRequest.upgradeCompleteLookupIDs = new Array();
    this.rpcStatusFilterRequest.upgradeComplete.forEach(x => {
      var ucLookup = this.ivendLookups.find(lookup => lookup.typeName === 'RPCTrackStatusUpgradeComplete' && lookup.enumerationValue === x);
      this.rpcStatusFilterRequest.upgradeCompleteLookupIDs.push(ucLookup.iVendLookupID);
    });
    this.rpcStatusFilterRequest.testingStatusLookupIDs = new Array();
    this.rpcStatusFilterRequest.testingStatus.forEach(x => {
      var tsLookup = this.ivendLookups.find(lookup => lookup.typeName === 'RPCTrackStatusTestingStatus' && lookup.enumerationValue === x);
      this.rpcStatusFilterRequest.testingStatusLookupIDs.push(tsLookup.iVendLookupID);
    });
    this.rpcStatusFilterRequest.testingCompleteLookupIDs = new Array();
    this.rpcStatusFilterRequest.testingComplete.forEach(x => {
      var tcLookup = this.ivendLookups.find(lookup => lookup.typeName === 'RPCTrackStatusTestingComplete' && lookup.enumerationValue === x);
      this.rpcStatusFilterRequest.testingCompleteLookupIDs.push(tcLookup.iVendLookupID);
    });
  }

  private getRouteArray() {
    this.routeIDList = new Array();
    if (this._filterBarService.routes.value) {
      for (let i = 0; i < this._filterBarService.routes.value.length; i++) {
        if(this._filterBarService.routes.value[i].selected) {  
          this.routeIDList.push(this._filterBarService.routes.value[i].routeID);
        }
      }
    }  
    return this.routeIDList;
  }

  private getAccLocationArray() { 
    this.accLocList = new Array();
    if (this._filterBarService.accountLocationList.value != null) {
      for (let i = 0; i < this._filterBarService.accountLocationList.value.length; i++) {
        if(this._filterBarService.accountLocationList.value[i].selected) {
          this.accLocList.push(this._filterBarService.accountLocationList.value[i].id);
        }
      }
    }
   return this.accLocList;
  }

  private isDisabled(item, fieldName) {
    let returnValue = false;
    switch(fieldName) {
      case 'upgradeTechnicianName':
        returnValue = item.upgradeComplete != 2 /* Not Yes */;
        break;
    }
    return returnValue;
  }

  private isHidden(item, fieldName) {
    let returnValue = false;
    switch(fieldName) {
      case 'upgradeTechnicianName':
        returnValue = item.upgradeComplete != 2 /* Is not Yes */;
        break;
      case 'upgradeCompletedDateTime':
        returnValue = item.upgradeCompletedDateTime != null;
        break;
    }
    return returnValue;
  }

  isUpgradeNeededNo(item: RPCTrackStatusItem): boolean {
    const UpgradeNeededNo = this.ivendLookups.find(x => x.typeName === 'RPCTrackStatusUpgradeNeeded' && x.enumerationValue === 'No');
    return UpgradeNeededNo && item.upgradeNeeded == UpgradeNeededNo.iVendLookupID;
  }

  private onChange(item : RPCTrackStatusItem, fieldName){
    const UpgradeNeededNo = this.ivendLookups.find(x => x.typeName === 'RPCTrackStatusUpgradeNeeded' && x.enumerationValue === 'No');
    const UpgradeCompletedNA = this.ivendLookups.find(x => x.typeName === 'RPCTrackStatusUpgradeComplete' && x.enumerationValue === 'N/A');
    const UpgradeCompleteNo = this.ivendLookups.find(x => x.typeName === 'RPCTrackStatusUpgradeComplete' && x.enumerationValue === 'No');

    switch(fieldName){
      case 'upgradeComplete':
        item.upgradeComplete = Number(item.upgradeComplete);
        if (item.upgradeComplete == 2) {
          item.upgradeCompletedDateTime = new Date();
        } else {
          item.upgradeCompletedDateTime = null;
          item.upgradeTechnicianName = null;
        }
        break;
      case 'upgradeNeeded':
        item.upgradeNeeded = Number(item.upgradeNeeded);
        if (UpgradeNeededNo && item.upgradeNeeded == UpgradeNeededNo.iVendLookupID) {
          item.upgradeComplete = UpgradeCompletedNA ? UpgradeCompletedNA.iVendLookupID : 3;
          item.upgradeTechnicianName = null;
          item.upgradeCompletedDateTime = null;
        } else {
          item.upgradeNotNeededReason = null;
          item.upgradeNotNeededReasonLookupID = null;
          // Retain UpgradeComplete and Technician if already completed (UpgradeComplete=2 with technician name)
          if (item.upgradeComplete == 2 && item.upgradeTechnicianName && item.upgradeTechnicianName.trim().length > 0) {
            // Keep upgradeComplete, upgradeTechnicianName, and upgradeCompletedDateTime as-is
          } else {
            if (UpgradeCompleteNo) {
              item.upgradeComplete = UpgradeCompleteNo.iVendLookupID;
            }
            item.upgradeCompletedDateTime = null;
            item.upgradeTechnicianName = null;
          }
        }
        break;
      case 'upgradeNotNeededReason':
        const matchedReason = this.upgradeNotNeededReasons.find(r => r.description === item.upgradeNotNeededReason);
        item.upgradeNotNeededReasonLookupID = matchedReason ? matchedReason.iVendLookupID : null;
        break;
      case 'upgradeTechnicianName':
        break;
    }
  }

  private onInput(item : RPCTrackStatusItem, fieldName:string) {
    switch(fieldName){
      case 'upgradeTechnicianName':
        this.markAsDirty(item.rpcTrackStatusID);
        break;
    }
  }

  private sortOpenOperationStatuses(event:any, sortColumn:string) {
    this.rpcStatusFilterRequest.sortColumn = sortColumn;
    this.rpcStatusFilterRequest.sortDirection = event ? "ASC":"DESC";
    this.onApplyFilters(false);
  }

  private getExcelTransformedItems(data: RPCTrackStatusResponse):RPCTrackStatusExcelItem[] {
    let excelItems: RPCTrackStatusExcelItem[] = new Array();

    for (let index = 0; index <  data.items.length; index++) {
      const item = data.items[index];
      const excelItem:RPCTrackStatusExcelItem = new RPCTrackStatusExcelItem();
      excelItem.Customer            = item.customer;
      excelItem.RouteNumber         = item.routeNumber;
      excelItem.AccountLocation     = item.accountName + "/" + item.locationName;
      excelItem.MachineNumber       = item.machineNumber;
      excelItem.MachineSerialNumber = item.machineSerialNumber;
      excelItem.DEXSerialNumber     = item.dexSerialNumber;
      excelItem.UpdatedMachineModel = item.updatedMachineModel;
      excelItem.CapitalAssetNumber  = item.capitalAssetNumber;
      excelItem.VendorCode          = item.vendorCode;
      excelItem.DeviceNo            = item.deviceNo;
      excelItem.UpgradeSteps        = item.upgradeSteps;
      excelItem.PartNumber          = item.partNumber;
      excelItem.UpgradeNeeded       = item.upgradeNeededDisplay;
      excelItem.Reason              = item.upgradeNotNeededReason || '';
      excelItem.UpgradeComplete     = item.upgradeCompleteDisplay;
      excelItem.TechnicianName      = item.upgradeTechnicianName;
      excelItem.CompletedDate       = item.upgradeCompletedDateTime == null ? '' : moment(item.upgradeCompletedDateTime).format ("MM/DD/YYYY");
      excelItem.TestingStatus       = item.testingStatusDisplay;
      excelItem.TestingComplete     = item.testingCompleteDisplay;

      excelItems.push(excelItem);
    }

    return excelItems;
  }

  private exportToExcel() {
    this.responseMessage = "";
    this.errorMessage = "";
    this.isProcessing = true;
    const allItemsFilteredRequest = {...this.rpcStatusFilterRequest};
    allItemsFilteredRequest.pageSize = 100000;
    allItemsFilteredRequest.pageNumber = 1;
    console.log(`Export to Excel filter: ${JSON.stringify(allItemsFilteredRequest)}`);
    
    this.rpcTrackStatusItemSubject = this._rpcService.GetRPCTrackStatusItems(allItemsFilteredRequest).subscribe(
      (data: RPCTrackStatusResponse) => {
        console.log("Excel Export Result::",data);
        this.setItemsFieldsFromLookups(data.items, data.iVendLookups);
        let excelItems = this.getExcelTransformedItems(data);

        const dateFormat = "YYYYMMDD";
        let _now: Moment = moment(new Date());
        const fileName = 'RPC Tracking Status' + '_' + _now.format(dateFormat);
          this._excel.exportAsExcelFile(excelItems, fileName);
        this.isProcessing = false;
      }),
      (error => {
        console.log("Excel Export fail");
        this.errorMessage = `An error has occurred.  Please try again.  Message=${error}`;
        this.isProcessing = false;
      });
  } // end exportToExcel

  markAsDirty(rpcTrackStatusID: number) {
    let rowToMark = this.rpcStatusItems.find(x => x.rpcTrackStatusID === rpcTrackStatusID);
    rowToMark.dirty = true;
    rowToMark.invalidUpdate = !this.isUpdateValid(rowToMark);
    this.enableSaveButton = !this.rpcStatusItems.some(x => x.invalidUpdate);
    this.tableIsDirty = true;
  }

  private isUpdateValid(item: RPCTrackStatusItem) {
    const UpgradeNeededYes = this.ivendLookups.find(x => x.typeName ==='RPCTrackStatusUpgradeNeeded' && x.enumerationValue === 'Yes').iVendLookupID;
    const UpgradeNeededNo  = this.ivendLookups.find(x => x.typeName ==='RPCTrackStatusUpgradeNeeded' && x.enumerationValue === 'No').iVendLookupID;
    const UpgradeCompleteNo  = this.ivendLookups.find(x => x.typeName ==='RPCTrackStatusUpgradeComplete' && x.enumerationValue === 'No').iVendLookupID;
    const UpgradeCompletedYes = this.ivendLookups.find(x => x.typeName ==='RPCTrackStatusUpgradeComplete' && x.enumerationValue === 'Yes').iVendLookupID;
    const UpgradeCompletedNA = this.ivendLookups.find(x => x.typeName ==='RPCTrackStatusUpgradeComplete' && x.enumerationValue === 'N/A').iVendLookupID;

    // Upgrade Needed = No: must have a reason selected
    if (item.upgradeNeeded == UpgradeNeededNo) {
      return !!item.upgradeNotNeededReason && item.upgradeNotNeededReason.length > 0;
    }

    // Upgrade Needed = Yes
    if (item.upgradeNeeded == UpgradeNeededYes) {
      if (item.upgradeComplete == UpgradeCompletedYes) {
        return item.upgradeTechnicianName != null && item.upgradeTechnicianName.trim().length > 0;
      }
      return true;
    }

    return true;
  }

  private saveButton() {
    const items:RPCTrackStatusItem[] = new Array();
    for (let i = 0; i < this.rpcStatusItems.length; i++) {
      const item = this.rpcStatusItems[i];
      if (item.dirty) {
        items.push(item);
      }
    }

    if (items.length > 0) {
      this.isProcessing = true;
      this.errorMessage = "";
      this._rpcService.PutRPCTrackStatusItems(items, this._security.getUserID()).subscribe(
      (data:RPCTrackStatusSaveResponse) => {
        if (data.exceptions.length == 0) {
          for(let i=0; i< data.items.length; i++) {
            const item = data.items[i];
            if (item.exceptions.length == 0) {
              // Since there are no exceptions returned for this item, un-mark the item as dirty
              const rpcItemIndex = this.rpcStatusItems.findIndex(x=>x.rpcTrackStatusID == item.rpcTrackStatusID);
              item.dirty = false;
              this.setItemFieldsFromLookups(item, this.ivendLookups);
              item.accountLocation = item.accountName + '\r\n' + item.locationName;
              item.vendorCode = item.vendorCode ? item.vendorCode.slice(0, 5) : item.vendorCode;
              if (rpcItemIndex >= 0) {
                this.rpcStatusItems[rpcItemIndex]=item;
              }
            }
          }
          this.tableIsDirty = false;
          this.loadSummaryData();
        } else {
          // Show the exceptions on page
          let message = "Failed to save. ";
          data.exceptions.forEach(ex => {
            message += ex.message + "; ";
          });
          this.errorMessage = message;
        }
        console.log('Save completed');
      }),
      (error)=>{
        console.log(`Failure to put items ${error}`);
      };
    }

    this.isProcessing = false;
  }

  pageChanged() {
    window.scrollTo(0, 0);
    this.onApplyFilters(false);
  }

  toggleInfoPopover(event: MouseEvent): void {
    event.stopPropagation();
    this.showInfoPopover = !this.showInfoPopover;
  }

  closeInfoPopover(): void {
    this.showInfoPopover = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showInfoPopover) return;

    const target = event.target as HTMLElement;
    const container = document.querySelector('.info-button-container');

    if (container && !container.contains(target)) {
      this.closeInfoPopover();
    }
  }

  onChangeView(view) {
    this.view = view;
    if (this.isMobile) {
      this.filterPosition = 'out';
    } else {
      this.filterPosition = view === 'grid' ? 'out' : 'in';
    }
  }
}
